import React, { useState, useEffect } from "react";
import { db, storage } from "../firebase";
import {
  collection,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  updateDoc
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { Add, Edit2, Trash, Setting4, DocumentUpload } from "iconsax-reactjs";
import { FACILITY_ICONS, getIconComponent } from "../utils/iconMap";
import { TAXONOMY } from "../shared/listingRules";
import "./FacilitiesPanel.css";

// Amenities are what the property offers (gym, pool, lift); nearby places
// describe its surroundings (school, hospital, metro). The app and both web
// forms list them separately, and search filters use amenities only.
const FACILITY_GROUPS = TAXONOMY.options.facilityGroups;
const groupOf = (fac) => (fac.group === "nearby" ? "nearby" : "amenity");
const groupLabel = (id) => (id === "nearby" ? "Nearby place" : "Amenity");

// Section heading and one-line explainer per group on the facilities page.
const GROUP_SECTIONS = {
  amenity: { title: "Amenities", hint: "Part of the property, like a lift, gym or power backup. Search filters use these.", empty: "No amenities yet." },
  nearby: { title: "Nearby places", hint: "What is around the property, like a school, hospital or metro.", empty: "No nearby places yet." },
};

export default function FacilitiesPanel() {
  const [facilities, setFacilities] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [name, setName] = useState("");
  const [selectedIconName, setSelectedIconName] = useState("");
  const [selectedSvgFile, setSelectedSvgFile] = useState(null);
  const [selectedSvgPreview, setSelectedSvgPreview] = useState(null);
  const [residential, setResidential] = useState(true);
  const [commercial, setCommercial] = useState(true);
  const [group, setGroup] = useState("amenity");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const facilitiesCol = collection(db, "facilities");
    const q = query(facilitiesCol, orderBy("createdAt", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = [];
      snapshot.forEach((docSnap) => {
        list.push({ id: docSnap.id, ...docSnap.data() });
      });
      setFacilities(list);
      setLoaded(true);
    }, (err) => {
      console.error("Failed to load facilities:", err);
      setLoaded(true);
    });

    return () => unsubscribe();
  }, []);

  const handleSvgChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "image/svg+xml") {
        alert("Please select a valid SVG file.");
        return;
      }
      if (file.size > 500000) {
        alert("Please select an SVG smaller than 500KB.");
        return;
      }
      setSelectedSvgFile(file);
      setSelectedSvgPreview(URL.createObjectURL(file));
      setSelectedIconName(""); // clear standard icon
    }
  };

  const clearSvg = () => {
    setSelectedSvgFile(null);
    setSelectedSvgPreview(null);
  };

  const handleEdit = (fac) => {
    setEditingFacility(fac);
    setName(fac.name);
    setResidential(fac.residential !== false);
    setCommercial(fac.commercial !== false);
    setGroup(groupOf(fac));
    if (fac.iconType === "custom") {
      setSelectedIconName("");
      setSelectedSvgFile(null);
      setSelectedSvgPreview(fac.iconUrl);
    } else {
      setSelectedIconName(fac.iconName || "");
      setSelectedSvgFile(null);
      setSelectedSvgPreview(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingFacility(null);
    setName("");
    setSelectedIconName("");
    setResidential(true);
    setCommercial(true);
    setGroup("amenity");
    clearSvg();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      alert("Please enter a facility name.");
      return;
    }
    if (!selectedIconName && !selectedSvgFile && !selectedSvgPreview) {
      alert("Please select a standard icon or upload a custom SVG.");
      return;
    }

    setIsSaving(true);
    try {
      let iconUrl = editingFacility?.iconUrl || "";
      let iconType = selectedSvgFile || selectedSvgPreview ? "custom" : "library";

      if (selectedSvgFile) {
        const filename = `facilities/icons/${Date.now()}_${name.trim().replace(/\s+/g, '_')}.svg`;
        const storageRef = ref(storage, filename);
        const uploadTask = uploadBytesResumable(storageRef, selectedSvgFile);
        
        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            null,
            reject,
            async () => {
              iconUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      } else if (!selectedSvgPreview) {
        iconUrl = "";
      }

      const facData = {
        name: name.trim(),
        iconType,
        iconName: iconType === "library" ? selectedIconName : null,
        iconUrl: iconType === "custom" ? iconUrl : null,
        residential: residential,
        commercial: commercial,
        group,
      };

      if (editingFacility) {
        await updateDoc(doc(db, "facilities", editingFacility.id), facData);
      } else {
        await addDoc(collection(db, "facilities"), {
          ...facData,
          createdAt: Date.now()
        });
      }

      handleCloseModal();
    } catch (err) {
      console.error("Failed to save facility:", err);
      alert("Error saving facility.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this facility? It will be removed globally from all properties.")) {
      try {
        await deleteDoc(doc(db, "facilities", id));
      } catch (err) {
        console.error("Failed to delete facility:", err);
        alert("Error deleting facility.");
      }
    }
  };

  const sections = FACILITY_GROUPS.map((g) => ({
    id: g.id,
    ...(GROUP_SECTIONS[g.id] || { title: g.label, hint: "", empty: "Nothing here yet." }),
    items: facilities.filter((fac) => groupOf(fac) === g.id),
  }));
  const amenityCount = facilities.filter((fac) => groupOf(fac) === "amenity").length;
  const nearbyCount = facilities.length - amenityCount;

  const renderFacilityIcon = (fac) => {
    if (fac.iconType === "custom" && fac.iconUrl) {
      return <img src={fac.iconUrl} alt="" />;
    }
    const IconComp = getIconComponent(fac.iconName);
    return <IconComp size={18} variant="Bold" color="currentColor" />;
  };

  return (
    <div className="fp">
      <div className="toolbar">
        <p className="fp-summary">
          {loaded
            ? `${amenityCount} ${amenityCount === 1 ? "amenity" : "amenities"}, ${nearbyCount} ${nearbyCount === 1 ? "nearby place" : "nearby places"}`
            : "Loading facilities"}
        </p>
        <div className="toolbar-spacer" />
        <button type="button" className="btn-primary fp-add" onClick={() => setIsModalOpen(true)}>
          <Add size={18} variant="Linear" aria-hidden="true" />
          Add facility
        </button>
      </div>

      {!loaded ? (
        <ul className="fp-grid" aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => (
            <li key={i} className="fp-tile">
              <span className="skeleton fp-sk-icon" />
              <span className="fp-tile-text">
                <span className="skeleton fp-sk-line" />
                <span className="skeleton fp-sk-line is-short" />
              </span>
            </li>
          ))}
        </ul>
      ) : facilities.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span className="empty-state-icon"><Setting4 size={22} variant="Linear" aria-hidden="true" /></span>
            <h3>No facilities yet</h3>
            <p>Add amenities and nearby places here. They appear as options on listing forms and in the mobile app.</p>
          </div>
        </div>
      ) : (
        sections.map((section) => (
          <section key={section.id} className="fp-section" aria-labelledby={`fp-section-${section.id}`}>
            <div className="fp-section-head">
              <h2 id={`fp-section-${section.id}`} className="fp-section-title">
                {section.title}
                <span className="count-badge">{section.items.length}</span>
              </h2>
              {section.hint && <p className="fp-section-hint">{section.hint}</p>}
            </div>
            {section.items.length === 0 ? (
              <p className="fp-group-empty">{section.empty}</p>
            ) : (
              <ul className="fp-grid">
                {section.items.map((fac) => (
                  <li key={fac.id} className="fp-tile">
                    <span className="fp-icon" aria-hidden="true">{renderFacilityIcon(fac)}</span>
                    <div className="fp-tile-text">
                      <span className="fp-name" title={fac.name}>{fac.name}</span>
                      <span className="fp-meta">
                        {fac.residential !== false && <span className="chip">Residential</span>}
                        {fac.commercial !== false && <span className="chip">Commercial</span>}
                        <span className="fp-source">{fac.iconType === "custom" ? "Custom icon" : "Standard icon"}</span>
                      </span>
                    </div>
                    <div className="fp-tile-actions">
                      <button type="button" className="btn-icon" onClick={() => handleEdit(fac)} title="Edit" aria-label={`Edit ${fac.name}`}>
                        <Edit2 size={18} variant="Linear" />
                      </button>
                      <button type="button" className="btn-icon fp-action-danger" onClick={() => handleDelete(fac.id)} title="Delete" aria-label={`Delete ${fac.name}`}>
                        <Trash size={18} variant="Linear" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal fp-modal" role="dialog" aria-modal="true" aria-labelledby="fp-modal-title">
            <div className="modal-header">
              <h2 id="fp-modal-title">{editingFacility ? "Edit facility" : "Add facility"}</h2>
              <button type="button" className="btn-icon" aria-label="Close" onClick={handleCloseModal}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </svg>
              </button>
            </div>
            <form className="fp-form" onSubmit={handleSave}>
              <div className="modal-body fp-form-body">
                <div className="field">
                  <label className="field-label" htmlFor="fp-name">Facility name</label>
                  <input
                    id="fp-name"
                    type="text"
                    className="input"
                    placeholder="e.g. Swimming pool"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="field">
                  <span className="field-label" id="fp-group-label">Group</span>
                  <div className="segmented fp-segmented" role="group" aria-labelledby="fp-group-label">
                    {FACILITY_GROUPS.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        aria-pressed={group === g.id}
                        onClick={() => setGroup(g.id)}
                      >
                        {groupLabel(g.id)}
                      </button>
                    ))}
                  </div>
                  <p className="field-hint">
                    Amenities are part of the property (lift, gym, power backup). Nearby places describe the area (school, hospital, metro). Search filters use amenities only.
                  </p>
                </div>

                <fieldset className="field fp-fieldset">
                  <legend className="field-label">Available categories</legend>
                  <div className="fp-checks">
                    <label className="fp-check">
                      <input
                        type="checkbox"
                        checked={residential}
                        onChange={(e) => setResidential(e.target.checked)}
                      />
                      Residential properties
                    </label>
                    <label className="fp-check">
                      <input
                        type="checkbox"
                        checked={commercial}
                        onChange={(e) => setCommercial(e.target.checked)}
                      />
                      Commercial properties
                    </label>
                  </div>
                </fieldset>

                <div className="field">
                  <span className="field-label">Custom SVG icon <span className="fp-optional">(optional)</span></span>
                  {!selectedSvgPreview ? (
                    <div className="fp-upload">
                      <input id="fp-svg" type="file" accept=".svg" className="fp-upload-input" onChange={handleSvgChange} />
                      <label htmlFor="fp-svg" className="fp-upload-label">
                        <DocumentUpload size={20} variant="Linear" aria-hidden="true" />
                        <span className="fp-upload-title">Choose an .svg file</span>
                        <span className="fp-upload-hint">Max 500KB</span>
                      </label>
                    </div>
                  ) : (
                    <div className="fp-svg-selected">
                      <span className="fp-icon"><img src={selectedSvgPreview} alt="Selected custom icon" /></span>
                      <span className="fp-svg-text">Custom SVG selected</span>
                      <button type="button" className="btn-secondary btn-sm ml-auto" onClick={clearSvg}>
                        Remove SVG
                      </button>
                    </div>
                  )}
                </div>

                {!selectedSvgPreview && (
                  <div className="field">
                    <span className="field-label" id="fp-icon-label">Or choose a standard icon</span>
                    <div className="fp-icon-grid" role="group" aria-labelledby="fp-icon-label">
                      {FACILITY_ICONS.map(({ name: iconName, label, component: IconComp }) => (
                        <button
                          key={iconName}
                          type="button"
                          className={`fp-icon-choice ${selectedIconName === iconName ? "is-selected" : ""}`}
                          aria-pressed={selectedIconName === iconName}
                          onClick={() => setSelectedIconName(iconName)}
                        >
                          <IconComp size={22} variant="Bold" color="currentColor" aria-hidden="true" />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={handleCloseModal} disabled={isSaving}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSaving}>
                  {isSaving && <span className="btn-spinner" aria-hidden="true" />}
                  {isSaving ? "Saving" : "Save facility"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
